import { useState } from 'react';
import { Input } from '../../components/ui/input';
import { Trash, Plus } from '../../design-system/icons';
import type { ProductInput } from '@discovr/types';
import { ActionConfirmDialog } from '../../components/common/ActionConfirmDialog';

const NAME_PLACEHOLDER = 'Product name';
const LINK_PLACEHOLDER = 'e.g. https://…';
const NOTE_PLACEHOLDER = 'Optional focus instruction';
const ADD_PRODUCT_LABEL = 'Add product';
const REMOVE_CONFIRM_TITLE = 'Remove product?';
const REMOVE_CONFIRM_LABEL = 'Remove product';
const DEFAULT_PRODUCT_LABEL = 'This product';
const removeConfirmDescription = (productName: string) =>
  `“${productName}” may be assigned to creators. Removing it here unassigns it from them when you save your changes.`;

// Stable client-only row key (not identity-keyed, to avoid remounting/losing
// focus on every keystroke); never sent to the server.
type ProductRow = ProductInput & { _key?: string };

let rowKeySeq = 0;
const mintRowKey = (): string => `product_row_${rowKeySeq++}`;

function keyForRow(row: ProductRow, index: number): string {
  return row._key ?? row.product_id ?? `product_row_fallback_${index}`;
}

function isSavedRow(row: ProductRow): boolean {
  return Boolean(row.product_id);
}

// Controlled product rows (name/link/note + Add/Remove). Caller owns persistence.
export function ProductRowsEditor({
  value,
  onChange,
}: {
  value: ProductInput[];
  onChange: (next: ProductInput[]) => void;
}) {
  const rows: ProductRow[] = value;
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

  const updateRow = (index: number, patch: Partial<ProductInput>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const addRow = () => {
    const newRow: ProductRow = { name: '', product_url: '', notes: '', _key: mintRowKey() };
    onChange([...rows, newRow]);
  };

  const removeRow = (index: number) => onChange(rows.filter((_, i) => i !== index));

  const requestRemoveRow = (index: number) => {
    const row = rows[index];
    if (row && isSavedRow(row)) {
      setPendingDeleteIndex(index);
      return;
    }
    removeRow(index);
  };

  const cancelDelete = () => setPendingDeleteIndex(null);
  const confirmDelete = () => {
    if (pendingDeleteIndex !== null) removeRow(pendingDeleteIndex);
    cancelDelete();
  };

  const pendingDeleteRow = pendingDeleteIndex === null ? undefined : rows[pendingDeleteIndex];
  const pendingDeleteName = pendingDeleteRow?.name?.trim() || DEFAULT_PRODUCT_LABEL;

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3">
          {rows.map((row, index) => (
            <ProductRowFields
              key={keyForRow(row, index)}
              row={row}
              index={index}
              onChange={(patch) => updateRow(index, patch)}
              onRemove={() => requestRemoveRow(index)}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={addRow}
          className="flex items-center gap-1.5 self-start rounded-lg border border-dashed border-border px-3 py-2 text-body-sm-medium text-primary transition-colors hover:bg-muted"
        >
          <Plus size={14} weight="bold" /> {ADD_PRODUCT_LABEL}
        </button>
      </div>

      {pendingDeleteIndex !== null && (
        <ActionConfirmDialog
          open
          variant="delete"
          title={REMOVE_CONFIRM_TITLE}
          description={removeConfirmDescription(pendingDeleteName)}
          confirmLabel={REMOVE_CONFIRM_LABEL}
          onOpenChange={(isOpen) => {
            if (!isOpen) cancelDelete();
          }}
          onConfirm={confirmDelete}
        />
      )}
    </>
  );
}

function ProductRowFields({
  row,
  index,
  onChange,
  onRemove,
}: {
  row: ProductInput;
  index: number;
  onChange: (patch: Partial<ProductInput>) => void;
  onRemove: () => void;
}) {
  const rowNumber = index + 1;
  return (
    <div className="grid grid-cols-1 items-start gap-2 sm:grid-cols-[1.2fr_1.2fr_1.6fr_auto] sm:gap-3">
      <Input
        value={row.name}
        onChange={(e) => onChange({ name: e.target.value })}
        placeholder={NAME_PLACEHOLDER}
        aria-label={`Product ${rowNumber} name`}
      />
      <Input
        value={row.product_url ?? ''}
        onChange={(e) => onChange({ product_url: e.target.value })}
        placeholder={LINK_PLACEHOLDER}
        aria-label={`Product ${rowNumber} link`}
      />
      <Input
        value={row.notes ?? ''}
        onChange={(e) => onChange({ notes: e.target.value })}
        placeholder={NOTE_PLACEHOLDER}
        aria-label={`Product ${rowNumber} note`}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove product ${rowNumber}`}
        className="flex size-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:text-(--foreground-destructive-default)"
      >
        <Trash size={16} />
      </button>
    </div>
  );
}
